import { useEffect, useState } from "react";
import EmailGate from "../components/EmailGate";
import { hasStoredContentAccess } from "../lib/gatedContentTracking";

const FEES_ACCESS_KEY = "asha_os_fees_2to4loc_access";

const STYLES = `
.pms-quote-doc {
  --paper: #fcfcfd;
  --paper-2: #f4f5f8;
  --ink: #1c2540;
  --ink-2: #404a66;
  --ink-3: #737d96;
  --line: #d8dde6;
  --line-2: #e7eaf0;
  --accent: #3a5bd9;
  --accent-deep: #2638b2;
  --accent-soft: #eef0fa;
  --accent-ink: #2c43c4;
  --accent-soft-2: #eaedf7;
  --accent-border: #bdc6e8;

  font-family: 'Inter', system-ui, sans-serif;
  color: var(--ink);
  font-size: 14px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-feature-settings: 'ss01', 'cv11';
}

.pms-quote-doc *,
.pms-quote-doc *::before,
.pms-quote-doc *::after { box-sizing: border-box; }

.pms-quote-doc .page {
  max-width: 1440px;
  margin: 40px auto;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(0,0,0,0.02),
    0 30px 60px -30px rgba(20,30,40,0.18),
    0 12px 24px -16px rgba(20,30,40,0.10);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
@media (min-width: 901px) {
  .pms-quote-doc .page {
    height: calc(100vh - 80px);
  }
}

.pms-quote-doc .topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 72px;
  border-bottom: 1px solid var(--line);
  background: var(--paper);
  flex-shrink: 0;
}
.pms-quote-doc .brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .brand-mark {
  width: 28px; height: 28px;
  border-radius: 7px;
  overflow: hidden;
  flex-shrink: 0;
}
.pms-quote-doc .brand-mark img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}
.pms-quote-doc .brand-meta {
  color: var(--ink-3);
  font-weight: 450;
}
.pms-quote-doc .topbar-right {
  display: flex;
  gap: 12px;
  align-items: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-3);
}
.pms-quote-doc .pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.pms-quote-doc .pill::before {
  content: "";
  width: 6px; height: 6px;
  border-radius: 99px;
  background: var(--accent);
  box-shadow: 0 0 0 3px rgba(238, 240, 250, 0.7);
}

.pms-quote-doc .hero {
  padding: 64px 72px 48px;
  border-bottom: 1px solid var(--line);
  position: relative;
  background:
    radial-gradient(1200px 320px at 90% -100px, rgba(216, 222, 248, 0.55), transparent 60%),
    var(--paper);
}
.pms-quote-doc .eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 18px;
}
.pms-quote-doc .eyebrow b { color: var(--accent-ink); font-weight: 500; }
.pms-quote-doc h1.title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 56px;
  line-height: 1.04;
  letter-spacing: -0.035em;
  margin: 0 0 14px;
  color: var(--ink);
  max-width: 18ch;
}
.pms-quote-doc .title-sub {
  font-family: 'Fraunces', serif;
  font-style: italic;
  font-weight: 300;
  font-size: 24px;
  letter-spacing: -0.01em;
  color: var(--accent-ink);
  margin: 0 0 26px;
}
.pms-quote-doc .lede {
  font-size: 17px;
  line-height: 1.55;
  color: var(--ink-2);
  max-width: 62ch;
  margin: 0 0 48px;
  text-wrap: pretty;
}

.pms-quote-doc .meta-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  margin-top: 8px;
}
.pms-quote-doc .meta-foot {
  margin: 14px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ink-3);
  font-style: italic;
}
.pms-quote-doc .meta-cell {
  padding: 18px 0;
  border-right: 1px solid var(--line-2);
}
.pms-quote-doc .meta-cell:first-child { padding-right: 36px; }
.pms-quote-doc .meta-cell:not(:first-child) { padding-left: 36px; padding-right: 36px; }
.pms-quote-doc .meta-cell:last-child { border-right: 0; padding-right: 0; }
.pms-quote-doc .meta-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 8px;
}
.pms-quote-doc .meta-value {
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .meta-value.placeholder {
  color: var(--ink-3);
  font-weight: 400;
}

.pms-quote-doc section.s {
  padding: 56px 72px;
  border-bottom: 1px solid var(--line);
}
.pms-quote-doc section.s:last-of-type { border-bottom: 0; }
.pms-quote-doc .s-head {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 32px;
  margin-bottom: 36px;
  align-items: baseline;
}
.pms-quote-doc .s-num {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  padding-top: 10px;
  border-top: 1px solid var(--ink);
  width: 52px;
}
.pms-quote-doc .s-title {
  font-family: 'Inter Tight', sans-serif;
  font-size: 34px;
  font-weight: 500;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: var(--ink);
  margin: 0;
}
.pms-quote-doc .s-sub {
  font-size: 15px;
  color: var(--ink-2);
  margin-top: 10px;
  max-width: 60ch;
  text-wrap: pretty;
}
.pms-quote-doc .s-sub b { color: var(--ink); font-weight: 500; }
.pms-quote-doc .s-body {
  margin-left: 152px;
}

.pms-quote-doc .contingency-note {
  padding: 22px 26px;
  margin: 0 0 32px;
  border: 1px solid var(--accent-border);
  border-radius: 12px;
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.7));
}
.pms-quote-doc .contingency-note p {
  margin: 0;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--ink-2);
}
.pms-quote-doc .contingency-note p + p {
  margin-top: 10px;
}
.pms-quote-doc .contingency-note b {
  color: var(--ink);
  font-weight: 500;
}

.pms-quote-doc .incl-grid {
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 0;
  border-top: 1px solid var(--line);
}
.pms-quote-doc .incl-col {
  padding: 28px 32px 28px 0;
  border-right: 1px solid var(--line-2);
}
.pms-quote-doc .incl-col + .incl-col {
  padding-left: 32px;
  padding-right: 0;
  border-right: 0;
}
.pms-quote-doc .incl-col-head {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 16px;
  row-gap: 2px;
  align-items: center;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--line-2);
}
.pms-quote-doc .incl-count {
  grid-row: 1 / span 2;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 38px;
  letter-spacing: -0.03em;
  line-height: 1;
  color: var(--ink);
}
.pms-quote-doc .incl-count.accent { color: var(--accent); }
.pms-quote-doc .incl-col-title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -0.015em;
  color: var(--ink);
  white-space: nowrap;
  align-self: end;
}
.pms-quote-doc .incl-col-sub {
  font-size: 12.5px;
  color: var(--ink-3);
  align-self: start;
}

.pms-quote-doc .prod-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.pms-quote-doc .prod-list li {
  padding-bottom: 14px;
  border-bottom: 1px dashed var(--line-2);
}
.pms-quote-doc .prod-list li:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.pms-quote-doc .prod-name {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pms-quote-doc .prod-sub {
  font-size: 12px;
  color: var(--ink-3);
  line-height: 1.5;
}
.pms-quote-doc .soon {
  display: inline-flex;
  padding: 1px 7px;
  border-radius: 4px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-family: 'JetBrains Mono', monospace;
  font-size: 9.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 500;
}

.pms-quote-doc .agent-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  counter-reset: agent;
}
.pms-quote-doc .agent-list li {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 13.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
  padding: 11px 14px 11px 28px;
  border-bottom: 1px solid var(--line-2);
  position: relative;
  counter-increment: agent;
}
.pms-quote-doc .agent-list li::before {
  content: counter(agent, decimal-leading-zero);
  position: absolute;
  left: 0;
  top: 12px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: var(--ink-3);
  font-weight: 400;
  letter-spacing: 0.04em;
}
.pms-quote-doc .agent-list li:nth-last-child(-n+2) { border-bottom: 0; }
.pms-quote-doc .agent-list li:nth-child(odd) { padding-right: 18px; }

.pms-quote-doc .price-stack {
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr;
  gap: 14px;
  align-items: stretch;
  margin-bottom: 32px;
}
.pms-quote-doc .price-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 24px 22px;
  background: var(--paper);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pms-quote-doc .price-card.accent {
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.7));
  border-color: var(--accent-border);
}
.pms-quote-doc .price-card.total {
  background: var(--ink);
  border-color: var(--ink);
  color: var(--paper);
}
.pms-quote-doc .price-card.total .price-tag,
.pms-quote-doc .price-card.total .price-desc { color: rgba(255,255,255,0.7); }
.pms-quote-doc .price-card.total .price-name,
.pms-quote-doc .price-card.total .price-amount { color: var(--paper); }
.pms-quote-doc .price-tag {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.pms-quote-doc .price-name {
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .price-amount {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 32px;
  letter-spacing: -0.025em;
  color: var(--ink);
  line-height: 1;
  margin-top: 4px;
}
.pms-quote-doc .price-amount small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 6px;
  letter-spacing: 0;
}
.pms-quote-doc .price-card.total .price-amount small { color: rgba(255,255,255,0.65); }
.pms-quote-doc .price-desc {
  font-size: 12px;
  color: var(--ink-2);
  line-height: 1.5;
  margin-top: 4px;
}
.pms-quote-doc .price-op {
  align-self: center;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 22px;
  color: var(--ink-3);
}

.pms-quote-doc .piecemeal-line {
  font-family: 'Inter', sans-serif;
  font-size: 19px;
  line-height: 1.6;
  color: var(--ink-2);
  margin: 0 0 22px;
}
.pms-quote-doc .piecemeal-line b {
  color: var(--ink);
  font-weight: 600;
}
.pms-quote-doc .piecemeal-line .piecemeal-num {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--accent-ink);
}

.pms-quote-doc .see-comparison {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 26px;
  margin: 24px 0 0;
  border: 1px solid var(--accent-border);
  border-radius: 12px;
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.7));
}
.pms-quote-doc .see-comparison-text {
  font-family: 'Inter Tight', sans-serif;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
  line-height: 1.45;
  margin: 0;
  flex: 1 1 auto;
  min-width: 240px;
}
.pms-quote-doc .see-comparison-text span {
  display: block;
  margin-top: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: var(--ink-3);
  letter-spacing: 0;
}
.pms-quote-doc .see-comparison-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  background: var(--accent);
  color: #fff;
  font-family: 'Inter Tight', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.005em;
  text-decoration: none;
  border: 1px solid var(--accent-deep);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.15) inset,
    0 2px 6px -2px rgba(38, 56, 178, 0.35);
  transition: background 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;
  white-space: nowrap;
}
.pms-quote-doc .see-comparison-btn:hover {
  background: var(--accent-deep);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.15) inset,
    0 4px 10px -2px rgba(38, 56, 178, 0.45);
}
.pms-quote-doc .see-comparison-btn:active {
  transform: translateY(1px);
}
.pms-quote-doc .see-comparison-btn .arrow {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 400;
  transition: transform 0.15s ease;
}
.pms-quote-doc .see-comparison-btn:hover .arrow {
  transform: translateX(2px);
}

.pms-quote-doc .fineprint {
  list-style: none;
  padding: 22px 0 0;
  margin: 32px 0 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-top: 1px solid var(--line-2);
}
.pms-quote-doc .fineprint li {
  font-size: 12.5px;
  color: var(--ink-3);
  line-height: 1.6;
  max-width: 88ch;
  padding-left: 18px;
  position: relative;
}
.pms-quote-doc .fineprint li::before {
  content: "";
  position: absolute;
  left: 0; top: 9px;
  width: 4px; height: 4px;
  border-radius: 99px;
  background: var(--ink-3);
}
.pms-quote-doc .fineprint .fp-label {
  color: var(--ink);
  font-weight: 500;
  font-family: 'Inter Tight', sans-serif;
  margin-right: 4px;
}
.pms-quote-doc .fineprint b { color: var(--ink); font-weight: 500; }

.pms-quote-doc table.lines {
  width: 100%;
  border-collapse: collapse;
  margin-top: 4px;
}
.pms-quote-doc table.lines th,
.pms-quote-doc table.lines td {
  text-align: left;
  padding: 22px 0;
  vertical-align: top;
  border-bottom: 1px solid var(--line-2);
}
.pms-quote-doc table.lines thead th {
  padding: 0 0 14px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
  border-bottom: 1px solid var(--ink);
}
.pms-quote-doc table.lines tbody tr:last-child td { border-bottom: 0; }
.pms-quote-doc .li-name {
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin-bottom: 6px;
}
.pms-quote-doc .li-desc {
  font-size: 13px;
  color: var(--ink-2);
  line-height: 1.5;
  max-width: 52ch;
}
.pms-quote-doc .li-amount {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -0.02em;
  color: var(--ink);
  white-space: nowrap;
}
.pms-quote-doc .li-amount.strike {
  text-decoration: line-through;
  text-decoration-color: var(--ink-3);
  color: var(--ink-3);
}
.pms-quote-doc .li-amount small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 2px;
  letter-spacing: 0;
}
.pms-quote-doc .li-amount .waived-badge {
  display: inline-block;
  margin-top: 6px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent-ink);
  background: var(--accent-soft);
  border: 1px solid var(--accent-border);
  padding: 3px 8px;
  border-radius: 4px;
}
.pms-quote-doc .li-note {
  font-size: 12.5px;
  color: var(--ink-2);
  line-height: 1.5;
}
.pms-quote-doc .col-item { width: 40%; padding-right: 32px !important; }
.pms-quote-doc .col-billing { width: 26%; padding-right: 32px !important; }
.pms-quote-doc .col-amount { width: 22%; text-align: right !important; padding-right: 32px !important; }
.pms-quote-doc .col-notes { width: 34%; padding-left: 0 !important; }
.pms-quote-doc td.col-amount,
.pms-quote-doc th.col-amount { text-align: right !important; }

.pms-quote-doc .concessions-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.pms-quote-doc .concessions-list li {
  padding: 22px 24px;
  border: 1px solid var(--accent-border);
  border-radius: 12px;
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.7));
}
.pms-quote-doc .concession-name {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 17px;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin-bottom: 8px;
}
.pms-quote-doc .concession-desc {
  font-size: 13.5px;
  color: var(--ink-2);
  line-height: 1.6;
}
.pms-quote-doc .concession-desc b { color: var(--ink); font-weight: 500; }

.pms-quote-doc .tail {
  padding: 18px 72px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--paper);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-3);
  border-top: 1px solid var(--line);
  flex-shrink: 0;
}

/* FAQ section */
.pms-quote-doc .faq-list {
  display: flex;
  flex-direction: column;
  gap: 22px;
  border-top: 1px solid var(--line);
  padding-top: 28px;
}
.pms-quote-doc .faq-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  padding-bottom: 22px;
  border-bottom: 1px dashed var(--line-2);
}
.pms-quote-doc .faq-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.pms-quote-doc .faq-q {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 18px;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 0;
}
.pms-quote-doc .faq-a {
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-2);
  max-width: 80ch;
  margin: 0;
}
.pms-quote-doc .faq-a b { color: var(--ink); font-weight: 500; }

/* SLIDESHOW */
.pms-quote-doc .slide {
  display: none;
}
.pms-quote-doc .slide.active {
  display: block;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  animation: pmsSlideIn 0.32s cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes pmsSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .pms-quote-doc .slide.active { animation: none; }
}

.pms-quote-doc .slide-nav {
  padding: 18px 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: var(--paper);
  border-top: 1px solid var(--line);
  flex-shrink: 0;
}
.pms-quote-doc .slide-nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 8px;
  background: var(--paper);
  color: var(--ink);
  font-family: 'Inter Tight', sans-serif;
  font-size: 13.5px;
  font-weight: 500;
  letter-spacing: -0.005em;
  border: 1px solid var(--line);
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease,
    color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}
.pms-quote-doc .slide-nav-btn:hover:not(:disabled) {
  background: var(--accent-soft);
  border-color: var(--accent-border);
  color: var(--accent-ink);
}
.pms-quote-doc .slide-nav-btn:active:not(:disabled) {
  transform: translateY(1px);
}
.pms-quote-doc .slide-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.pms-quote-doc .slide-nav-btn.primary {
  background: var(--accent);
  color: #fff;
  border-color: var(--accent-deep);
  box-shadow: 0 1px 0 rgba(255,255,255,0.15) inset,
    0 2px 6px -2px rgba(38,56,178,0.35);
}
.pms-quote-doc .slide-nav-btn.primary:hover:not(:disabled) {
  background: var(--accent-deep);
  color: #fff;
  border-color: var(--accent-deep);
  box-shadow: 0 1px 0 rgba(255,255,255,0.15) inset,
    0 4px 10px -2px rgba(38,56,178,0.45);
}

.pms-quote-doc .slide-progress {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: center;
  min-width: 0;
}
.pms-quote-doc .slide-dots {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pms-quote-doc .slide-dot {
  width: 8px;
  height: 8px;
  border-radius: 99px;
  background: var(--line);
  border: 0;
  padding: 0;
  cursor: pointer;
  transition: background 0.15s ease, width 0.2s ease;
}
.pms-quote-doc .slide-dot:hover {
  background: var(--ink-3);
}
.pms-quote-doc .slide-dot.active {
  background: var(--accent);
  width: 24px;
}
.pms-quote-doc .slide-counter {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-3);
  white-space: nowrap;
}
.pms-quote-doc .slide-counter b {
  color: var(--ink);
  font-weight: 500;
}

@media (max-width: 900px) {
  .pms-quote-doc .page { margin: 24px; }
  .pms-quote-doc .hero,
  .pms-quote-doc section.s,
  .pms-quote-doc .topbar,
  .pms-quote-doc .tail { padding-left: 28px; padding-right: 28px; }
  .pms-quote-doc h1.title { font-size: 44px; }
  .pms-quote-doc .s-head { grid-template-columns: 1fr; gap: 12px; }
  .pms-quote-doc .s-body { margin-left: 0; }
  .pms-quote-doc .meta-grid { grid-template-columns: 1fr 1fr; }
  .pms-quote-doc .incl-grid { grid-template-columns: 1fr; }
  .pms-quote-doc .incl-col { padding: 24px 0; border-right: 0; border-bottom: 1px solid var(--line-2); }
  .pms-quote-doc .incl-col + .incl-col { padding-left: 0; }
  .pms-quote-doc .price-stack { grid-template-columns: 1fr; }
  .pms-quote-doc .price-op { justify-self: center; }
  .pms-quote-doc .contingency-note { padding: 18px 20px; margin-bottom: 24px; }
}

@media (max-width: 720px) {
  .pms-quote-doc .page { margin: 14px; border-radius: 14px; }
  .pms-quote-doc .hero,
  .pms-quote-doc section.s,
  .pms-quote-doc .topbar,
  .pms-quote-doc .tail {
    padding-left: 20px; padding-right: 20px;
  }
  .pms-quote-doc .topbar { padding-top: 14px; padding-bottom: 14px; }
  .pms-quote-doc .topbar-right { gap: 8px; }
  .pms-quote-doc .hero { padding-top: 44px; padding-bottom: 36px; }
  .pms-quote-doc section.s { padding-top: 40px; padding-bottom: 40px; }

  .pms-quote-doc h1.title { font-size: 34px; max-width: none; }
  .pms-quote-doc .title-sub { font-size: 18px; margin-bottom: 20px; }
  .pms-quote-doc .lede { font-size: 15px; margin-bottom: 32px; }

  .pms-quote-doc .meta-grid { grid-template-columns: 1fr; }
  .pms-quote-doc .meta-cell {
    padding: 14px 0 !important;
    border-right: 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .meta-cell:last-child { border-bottom: 0; }

  .pms-quote-doc .price-amount { font-size: 28px; }

  .pms-quote-doc table.lines,
  .pms-quote-doc table.lines tbody,
  .pms-quote-doc table.lines tr,
  .pms-quote-doc table.lines td {
    display: block;
    width: auto !important;
  }
  .pms-quote-doc table.lines thead { display: none; }
  .pms-quote-doc table.lines tr {
    padding: 22px 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc table.lines tbody tr:first-child { padding-top: 18px; }
  .pms-quote-doc table.lines tbody tr:last-child { border-bottom: 0; }
  .pms-quote-doc table.lines td {
    padding: 0 !important;
    border: 0 !important;
  }
  .pms-quote-doc table.lines td + td { margin-top: 14px; }
  .pms-quote-doc table.lines td.col-amount { text-align: left !important; }
  .pms-quote-doc .li-desc,
  .pms-quote-doc .li-note { max-width: none; }

  .pms-quote-doc .tail {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding-top: 16px; padding-bottom: 16px;
  }

  .pms-quote-doc .slide-nav {
    padding: 14px 20px;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: space-between;
  }
  .pms-quote-doc .slide-progress {
    order: -1;
    flex-basis: 100%;
    justify-content: center;
    gap: 10px;
  }
  .pms-quote-doc .slide-nav-btn {
    padding: 9px 14px;
    font-size: 12.5px;
  }
  .pms-quote-doc .slide-counter {
    font-size: 10px;
  }
}

/* SLIDESHOW — PRINT OVERRIDES */
@media print {
  .pms-quote-doc .slide,
  .pms-quote-doc .slide.active {
    display: block !important;
    flex: none !important;
    min-height: 0 !important;
    overflow: visible !important;
    animation: none !important;
  }
  .pms-quote-doc .slide ~ .slide {
    break-before: page;
    page-break-before: always;
  }
  .pms-quote-doc .slide-nav { display: none !important; }
}

@media print {
  @page {
    size: letter;
    margin: 0.3in;
  }
  html, body {
    background: #fff !important;
  }
  body.pms-quote-body { background: #fff !important; }
  .pms-quote-doc { font-size: 11px !important; }
  .pms-quote-doc .page {
    max-width: 100% !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    height: auto !important;
    display: block !important;
  }
  .pms-quote-doc .topbar { padding: 14px 24px; }
  .pms-quote-doc .hero { padding: 32px 24px 24px; }
  .pms-quote-doc h1.title { font-size: 32px; max-width: none; }
  .pms-quote-doc .title-sub { font-size: 16px; margin-bottom: 14px; }
  .pms-quote-doc .lede { margin-bottom: 20px; font-size: 12px; }
  .pms-quote-doc section.s { padding: 24px; }
  .pms-quote-doc .s-head {
    grid-template-columns: 1fr;
    gap: 8px;
    margin-bottom: 16px;
  }
  .pms-quote-doc .s-title { font-size: 22px; }
  .pms-quote-doc .s-body { margin-left: 0; }
  .pms-quote-doc .tail { padding: 12px 24px; }

  .pms-quote-doc .meta-grid { grid-template-columns: repeat(2, 1fr); }
  .pms-quote-doc .incl-grid { display: block; }
  .pms-quote-doc .incl-col {
    padding: 18px 0;
    border-right: 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .incl-col:last-child { border-bottom: 0; }
  .pms-quote-doc .incl-col + .incl-col { padding-left: 0; }
  .pms-quote-doc .agent-list { grid-template-columns: 1fr 1fr 1fr; }
  .pms-quote-doc .price-stack { grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
  .pms-quote-doc .price-op { display: none; }
  .pms-quote-doc .price-card { padding: 14px; }
  .pms-quote-doc .price-amount { font-size: 22px; }

  .pms-quote-doc .meta-grid,
  .pms-quote-doc .price-card,
  .pms-quote-doc .contingency-note,
  .pms-quote-doc .concessions-list li,
  .pms-quote-doc table.lines tr,
  .pms-quote-doc .fineprint li,
  .pms-quote-doc .prod-list li,
  .pms-quote-doc .agent-list li {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .pms-quote-doc h1.title,
  .pms-quote-doc .s-title,
  .pms-quote-doc .prod-name,
  .pms-quote-doc .li-name,
  .pms-quote-doc .incl-col-head,
  .pms-quote-doc .s-head {
    break-after: avoid;
    page-break-after: avoid;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
`;

const SLIDES = [
  { id: "cover", label: "Cover" },
  { id: "included", label: "What's included" },
  { id: "comparison", label: "Pricing comparison" },
  { id: "pricing", label: "Our pricing" },
  { id: "onetime", label: "One-time fees" },
  { id: "concessions", label: "Special concessions" },
  { id: "faq", label: "FAQ" },
];

const OsFees2To4LocPage = () => {
  const [isLocked, setIsLocked] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const goPrev = () =>
    setCurrentSlide((i) => Math.max(i - 1, 0));
  const goNext = () =>
    setCurrentSlide((i) => Math.min(i + 1, SLIDES.length - 1));

  useEffect(() => {
    if (hasStoredContentAccess(FEES_ACCESS_KEY)) setIsLocked(false);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const tag = e.target && e.target.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (e.target && e.target.isContentEditable)
      ) {
        return;
      }
      if (e.key === "ArrowRight" || e.key === "PageDown") {
        setCurrentSlide((i) => Math.min(i + 1, SLIDES.length - 1));
        e.preventDefault();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        setCurrentSlide((i) => Math.max(i - 1, 0));
        e.preventDefault();
      } else if (e.key === "Home") {
        setCurrentSlide(0);
        e.preventDefault();
      } else if (e.key === "End") {
        setCurrentSlide(SLIDES.length - 1);
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const el = document.querySelector(
      `.pms-quote-doc .slide[data-slide-index="${currentSlide}"]`,
    );
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [currentSlide]);

  const handleAccessGranted = (email) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(FEES_ACCESS_KEY, "true");
    }
    // eslint-disable-next-line no-console
    console.log("Fee schedule access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    const link1 = document.createElement("link");
    link1.rel = "preconnect";
    link1.href = "https://fonts.googleapis.com";

    const link2 = document.createElement("link");
    link2.rel = "preconnect";
    link2.href = "https://fonts.gstatic.com";
    link2.crossOrigin = "";

    const link3 = document.createElement("link");
    link3.rel = "stylesheet";
    link3.href =
      "https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=Inter:wght@400;450;500;600&family=JetBrains+Mono:wght@400;500&family=Fraunces:opsz,wght@9..144,300;9..144,400&display=swap";

    document.head.appendChild(link1);
    document.head.appendChild(link2);
    document.head.appendChild(link3);

    const previousBg = document.body.style.background;
    const previousMargin = document.body.style.margin;
    document.body.style.background = "#f1efe7";
    document.body.style.margin = "0";
    document.body.classList.add("pms-quote-body");
    document.title = "Asha — Fee Schedule";

    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
      document.head.removeChild(link3);
      document.body.style.background = previousBg;
      document.body.style.margin = previousMargin;
      document.body.classList.remove("pms-quote-body");
    };
  }, []);

  return (
    <div className="pms-quote-doc">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {isLocked && <EmailGate onAccessGranted={handleAccessGranted} />}

      <div className="page">
        {/* TOP BAR */}
        <div className="topbar">
          <div className="brand">
            <div className="brand-mark">
              <img src="/assets/alogo.png" alt="Asha" />
            </div>
            <span>Asha Health</span>
            <span className="brand-meta">· asha.dental</span>
          </div>
          <div className="topbar-right">
            <span className="pill">Special Pricing</span>
            <span className="pill">Confidential</span>
          </div>
        </div>

        {/* HERO — slide 0 */}
        <div
          className={`hero slide ${currentSlide === 0 ? "active" : ""}`}
          data-slide-index="0"
        >
          <div className="eyebrow">
            <b>Fee Schedule</b> &nbsp;·&nbsp; Asha AI Operating System &nbsp;·&nbsp; <b>2–4 Location Pricing</b>
          </div>
          <h1 className="title">
            The AI Operating System for Modern Dental Offices
          </h1>
          <div className="title-sub">
            Special multi-location pricing for groups of 2–4 locations
          </div>
          <p className="lede">
            This fee schedule outlines special discounted pricing for a small
            group of <b style={{ color: "var(--ink)", fontWeight: 500 }}>two
            to four (2–4) locations</b> on the Asha platform. Pricing is per
            location, with the Software fee and Baseline AI package billed
            separately. The Setup &amp; Premium Onboarding Fee is{" "}
            <b style={{ color: "var(--ink)", fontWeight: 500 }}>waived</b> and
            IAM &amp; Multi-Location Management — typically reserved for
            groups of five or more locations — is{" "}
            <b style={{ color: "var(--ink)", fontWeight: 500 }}>included at
            no charge</b> under this quote. Final terms are governed by the
            executed Statement of Work and the Asha Terms of Service.
          </p>

          <div className="meta-grid">
            <div className="meta-cell">
              <div className="meta-label">Prepared by</div>
              <div className="meta-value">Asha Health, Inc.</div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">Quote valid until</div>
              <div className="meta-value">June 26th, 2026</div>
            </div>
          </div>
          <p className="meta-foot">
            Pricing in this quote is contingent on signing two to four (2–4)
            locations concurrently. This quote expires on June 26th, 2026.
          </p>
        </div>

        {/* § 01 — INCLUDED — slide 1 */}
        <section
          className={`s slide ${currentSlide === 1 ? "active" : ""}`}
          data-slide-index="1"
        >
          <div className="s-head">
            <div className="s-num">§ 01</div>
            <div>
              <h2 className="s-title">What's included</h2>
              <p className="s-sub">
                Every location ships with the full Asha software stack plus our
                baseline AI package. <b>IAM &amp; Multi-Location Management</b>
                {" "}— normally reserved for groups of five or more locations —
                is included at no charge under this quote so your group is
                managed as one operating unit from day one.
              </p>
            </div>
          </div>
          <div className="s-body">
            <div className="incl-grid">
              <div className="incl-col">
                <div className="incl-col-head">
                  <span className="incl-count">10</span>
                  <span className="incl-col-title">Softwares</span>
                  <span className="incl-col-sub">
                    Full Asha platform — included for every location at the
                    flat Software fee.
                  </span>
                </div>
                <ul className="prod-list">
                  <li>
                    <div className="prod-name">
                      Practice Management System{" "}
                      <span className="soon">PMS engagement only</span>
                    </div>
                    <div className="prod-sub">
                      Patient charts · Perio charting · Ledgers &amp; billing ·
                      Treatment planning · Scheduling · Clinical notes ·
                      Cloud-based · Multi-user access
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Communication Suite</div>
                    <div className="prod-sub">
                      VoIP phone system · Yealink desk phones · 2-way texting ·
                      Universal inbox · Team chat &amp; channels · Smart voice
                      logs · Intelligent fax · Next-gen digital forms
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Marketing Suite</div>
                    <div className="prod-sub">
                      SMS &amp; email campaigns · Automated workflows ·
                      Clinically-aware segmentation · AI handles patient
                      replies
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">eClaims Portal</div>
                    <div className="prod-sub">
                      Built-in eClaims · Clearinghouse connectivity · Single
                      login · Integrated submission
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Revenue Intelligence Suite</div>
                    <div className="prod-sub">
                      Reporting &amp; analytics dashboards · KPI tracking ·
                      Collection-rate insights · Revenue cycle analytics ·
                      Performance metrics
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">
                      Insurance Verification Software
                    </div>
                    <div className="prod-sub">
                      Automated verification · Real-time eligibility · Benefits
                      breakdown upload · Dual-source verification
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Integrated Payments Suite</div>
                    <div className="prod-sub">
                      Text-to-pay · Card readers · Multi-method support ·
                      Automated reminders
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Patient Portal</div>
                    <div className="prod-sub">
                      Secure patient links · Interactive forms · Treatment-plan
                      viewing · Insurance info upload
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">
                      ePrescribe{" "}
                      <span className="soon">PMS engagement · Coming soon</span>
                    </div>
                    <div className="prod-sub">
                      Electronic prescriptions · Integrated with PMS
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Online Scheduling</div>
                    <div className="prod-sub">
                      Patient self-scheduling · Real-time availability
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">
                      IAM &amp; Multi-Location Management{" "}
                      <span className="soon">Included · waived</span>
                    </div>
                    <div className="prod-sub">
                      Centralized identity &amp; access management · Roles
                      &amp; permissions across locations · Group-level
                      reporting &amp; admin · Normally for groups of 5+
                      locations
                    </div>
                  </li>
                </ul>
              </div>

              <div className="incl-col">
                <div className="incl-col-head">
                  <span className="incl-count accent">21</span>
                  <span className="incl-col-title">AI Products</span>
                  <span className="incl-col-sub">
                    Front-office, back-office, and clinical &amp; intelligence
                    AI — end-to-end, included in the Baseline AI package.
                  </span>
                </div>
                <ul className="agent-list">
                  <li>AI Receptionist</li>
                  <li>AI Schedule Optimizer</li>
                  <li>AI Hygiene Recall</li>
                  <li>AI Patient Experience</li>
                  <li>AI Case Acceptance</li>
                  <li>AI Patient Intake</li>
                  <li>AI Practice Manager</li>
                  <li>AI Fax Manager</li>
                  <li>AI Insurance Verification</li>
                  <li>AI Claim Submission</li>
                  <li>AI Claim Scrubbing</li>
                  <li>AI Denial Management</li>
                  <li>AI Patient Billing</li>
                  <li>AI AR &amp; Collections</li>
                  <li>AI EOB Posting</li>
                  <li>AI Insurance Calls</li>
                  <li>Asha Copilot</li>
                  <li>AI Scribe</li>
                  <li>Voice Perio</li>
                  <li>AI Treatment Planner</li>
                  <li>AI Revenue Analytics</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* § 02 — PRICING COMPARISON — slide 2 */}
        <section
          className={`s slide ${currentSlide === 2 ? "active" : ""}`}
          data-slide-index="2"
        >
          <div className="s-head">
            <div className="s-num">§ 02</div>
            <div>
              <h2 className="s-title">Pricing comparison</h2>
              <p className="s-sub">
                How Asha's all-in fee compares to assembling the same scope
                from other vendors at their actual published rates.
              </p>
            </div>
          </div>

          <div className="s-body">
            <p className="piecemeal-line">
              Asha's offering is so big that{" "}
              <b>
                piecemealing the same capabilities from other vendors would
                cost{" "}
                <span className="piecemeal-num">~$11,000 / month</span> +{" "}
                <span className="piecemeal-num">3.5% of collections</span>
              </b>{" "}
              per location — based on those vendors' actual published rack
              rates.
            </p>
            <div className="see-comparison">
              <p className="see-comparison-text">
                Don't believe it?
                <span>
                  See the full feature-by-feature breakdown.
                </span>
              </p>
              <a
                href="/os-fees-comparison"
                target="_blank"
                rel="noopener noreferrer"
                className="see-comparison-btn"
              >
                View pricing comparison
                <span className="arrow">→</span>
              </a>
            </div>
          </div>
        </section>

        {/* § 03 — OUR PRICING — slide 3 */}
        <section
          className={`s slide ${currentSlide === 3 ? "active" : ""}`}
          data-slide-index="3"
        >
          <div className="s-head">
            <div className="s-num">§ 03</div>
            <div>
              <h2 className="s-title">Our pricing for you</h2>
              <p className="s-sub">
                Per-location pricing is structured as a flat{" "}
                <b>Software fee</b> plus the <b>Baseline AI package</b> — a
                single all-in rate per location, with no separate add-ons for
                this group's size.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="contingency-note">
              <p>
                <b>
                  This special discounted pricing is contingent on signing
                  between two (2) and four (4) locations concurrently.
                </b>{" "}
                Under this quote, the per-location <b>Software fee</b> is held
                at $1,499 / month and the <b>Baseline AI package</b> is held
                at $499 / month. IAM &amp; Multi-Location Management
                (normally <b>$499 / month</b>, reserved for groups of 5+
                locations) is included at no charge.
              </p>
              <p>
                If the group expands to five or more locations, your account
                manager will roll the engagement onto the standard 5+
                location fee schedule. If fewer than two locations sign, the
                single-location fee schedule applies and the practice will be
                re-quoted accordingly.
              </p>
            </div>

            <div className="price-stack">
              <div className="price-card">
                <div className="price-tag">Component 1</div>
                <div className="price-name">Software</div>
                <div className="price-amount">
                  $1,499<small>/ location / month</small>
                </div>
                <div className="price-desc">
                  Flat monthly fee covering the 10 softwares Asha replaces and
                  unifies — PMS / continuous integration, communication suite,
                  marketing suite, eClaims, revenue intelligence, insurance
                  verification, payments, patient portal, online scheduling.
                </div>
              </div>
              <div className="price-op">+</div>
              <div className="price-card accent">
                <div className="price-tag">Component 2</div>
                <div className="price-name">Baseline AI Package</div>
                <div className="price-amount">
                  $499<small>/ location / month</small>
                </div>
                <div className="price-desc">
                  The full suite of 21 AI products across front-office,
                  back-office, and clinical &amp; intelligence — included in
                  the baseline package, billed per location.
                </div>
              </div>
              <div className="price-op">=</div>
              <div className="price-card total">
                <div className="price-tag">All-in total</div>
                <div className="price-name">Per Location</div>
                <div className="price-amount">
                  $1,998<small>/ location / month</small>
                </div>
                <div className="price-desc">
                  Single all-in monthly rate per location. IAM &amp;
                  Multi-Location Management is included at no charge.
                </div>
              </div>
            </div>

            <ul className="fineprint">
              <li>
                <span className="fp-label">All-in Flat Rate.</span>
                The per-location rate above is the sum of the <b>$1,499</b>
                {" "}Software fee and the <b>$499</b> Baseline AI package.
                There is no separate IAM / multi-location management fee
                charged for this engagement.
              </li>
              <li>
                <span className="fp-label">2–4 Location Contingency.</span>
                Pricing in this quote is contingent on executing Statements of
                Work for <b>two (2) to four (4) locations</b> concurrently. At
                five or more locations, the engagement transitions to the
                standard 5+ location fee schedule (which adds the $499 / month
                IAM &amp; Multi-Location Management fee).
              </li>
              <li>
                <span className="fp-label">IAM &amp; Multi-Location Management — Waived.</span>
                Normally <b>$499 / month</b> per group and reserved for groups
                of five or more locations. Under this quote it is{" "}
                <b>included at no charge</b>, giving you centralized identity,
                roles &amp; permissions, and group-level admin from day one
                across all signed locations.
              </li>
              <li>
                <span className="fp-label">Monthly Retainer &amp; Billing.</span>
                On the first day of each billing month, the Practice pays{" "}
                <b>$1,998 per location</b> ($1,499 Software + $499 Baseline
                AI). Billed per location, in advance.
              </li>
              <li>
                <span className="fp-label">AI Credits — Aggregate Pool.</span>
                <b> 2,000,000 credits</b> are included{" "}
                <b>in aggregate across all signed locations</b>, each month,
                pooled for utilization. We specify AI credit limits because
                running AI models is expensive and uncapped usage would risk
                us losing money on contracts. Overages bill at{" "}
                <b>$0.005 per additional credit</b>. Your account manager can
                share utilization-management techniques &amp; tools to help
                your team stay within the included allotment.
              </li>
              <li>
                <span className="fp-label">Contract Term.</span>
                Engagement is <b>month-to-month</b> per location — no
                long-term commitment required.
              </li>
            </ul>
          </div>
        </section>

        {/* § 04 — ONE-TIME — slide 4 */}
        <section
          className={`s slide ${currentSlide === 4 ? "active" : ""}`}
          data-slide-index="4"
        >
          <div className="s-head">
            <div className="s-num">§ 04</div>
            <div>
              <h2 className="s-title">One-time fees &amp; deposits</h2>
              <p className="s-sub">
                Charged once per location at signing. Under this quote, the
                Setup &amp; Premium Onboarding Fee is <b>waived</b>.
              </p>
            </div>
          </div>

          <div className="s-body">
            <table className="lines">
              <thead>
                <tr>
                  <th className="col-item">Item</th>
                  <th className="col-amount">Amount</th>
                  <th className="col-notes">Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="col-item">
                    <div className="li-name">
                      Setup &amp; Premium Onboarding
                    </div>
                    <div className="li-desc">
                      Implementation build-out, configuration, training, and
                      go-live support — delivered by a dedicated implementation
                      team for every signed location.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount strike">
                      $3,000<small>one-time / location</small>
                    </div>
                    <span className="waived-badge">Waived</span>
                  </td>
                  <td className="col-notes">
                    <div className="li-note">
                      <b>Waived</b> under this quote, contingent on the 2–4
                      location signing. Standard onboarding is included at no
                      cost for every signed location.
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Deposit</div>
                    <div className="li-desc">
                      Equal to one Monthly Retainer per location at the all-in
                      rate. Secures your onboarding start date and is applied
                      to the final month's invoice when the engagement ends.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      $1,998<small>per location</small>
                    </div>
                  </td>
                  <td className="col-notes">
                    <div className="li-note">
                      Collected upon signing. Refundable in the form of a
                      credit against the final month's invoice.
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* § 05 — SPECIAL CONCESSIONS — slide 5 */}
        <section
          className={`s slide ${currentSlide === 5 ? "active" : ""}`}
          data-slide-index="5"
        >
          <div className="s-head">
            <div className="s-num">§ 05</div>
            <div>
              <h2 className="s-title">Special concessions</h2>
              <p className="s-sub">
                Concessions offered as part of this 2–4 location quote, in
                addition to Asha's standard Statement of Work terms.
              </p>
            </div>
          </div>

          <div className="s-body">
            <ul className="concessions-list">
              <li>
                <div className="concession-name">
                  Onboarding Fee Waived — $3,000 / Location
                </div>
                <div className="concession-desc">
                  The standard <b>$3,000 one-time Setup &amp; Premium
                  Onboarding Fee per location</b> is waived for every signed
                  location under this quote. Asha covers the dedicated
                  implementation team at no cost to the Practice across all
                  2–4 locations.
                </div>
              </li>
              <li>
                <div className="concession-name">
                  IAM &amp; Multi-Location Management — Included
                </div>
                <div className="concession-desc">
                  Our <b>IAM &amp; Multi-Location Management</b> module —
                  normally a <b>$499 / month</b> add-on reserved for groups of
                  <b> five or more locations</b> — is included at no charge
                  under this quote. The group gets centralized identity,
                  roles, permissions, and group-level admin across all signed
                  locations from day one.
                </div>
              </li>
              <li>
                <div className="concession-name">
                  2,000,000 AI Credits — Aggregate Pool
                </div>
                <div className="concession-desc">
                  <b>2,000,000 AI credits per month are included in
                  aggregate</b> across all signed locations, pooled for
                  utilization rather than capped per location. Higher-volume
                  locations can draw against the unused allotment of lower-
                  volume locations, with overages billed at{" "}
                  <b>$0.005 per additional credit</b>.
                </div>
              </li>
            </ul>
          </div>
        </section>

        {/* § 06 — FAQ — slide 6 */}
        <section
          className={`s slide ${currentSlide === 6 ? "active" : ""}`}
          data-slide-index="6"
        >
          <div className="s-head">
            <div className="s-num">§ 06</div>
            <div>
              <h2 className="s-title">Frequently asked questions</h2>
              <p className="s-sub">
                Quick answers to the most common questions about how this
                quote applies to your group.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="faq-list">
              <div className="faq-item">
                <h3 className="faq-q">
                  Why is this quote structured for 2–4 locations specifically?
                </h3>
                <p className="faq-a">
                  Practices in this range are too small to fall under our
                  standard 5+ location enterprise pricing, but large enough
                  that they benefit materially from centralized IAM &amp;
                  multi-location management. This quote bridges that gap —
                  per-location rates stay flat at <b>$1,998 / month all-in</b>,
                  the <b>$3,000 onboarding fee per location is waived</b>, and
                  IAM management is <b>included at no charge</b>.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  What happens if the group grows past 4 locations?
                </h3>
                <p className="faq-a">
                  At five or more locations, the engagement transitions onto
                  the standard <b>5+ location fee schedule</b>, which adds the
                  <b> $499 / month</b> IAM &amp; Multi-Location Management
                  fee. Your account manager can walk through the standard 5+
                  pricing at that point.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  What happens if we only end up signing one location?
                </h3>
                <p className="faq-a">
                  The pricing above is{" "}
                  <b>contingent on signing two (2) to four (4) locations</b>{" "}
                  at execution. If only one location signs, the
                  single-location fee schedule applies and the practice will
                  be re-quoted — your account manager can walk through that
                  pricing.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  How does the 2,000,000 aggregate AI credit pool work?
                </h3>
                <p className="faq-a">
                  Instead of capping credits per location, all signed
                  locations share a single <b>2,000,000-credit pool</b> each
                  month. Higher-volume locations can draw against the unused
                  allotment of lower-volume locations. Overages bill at{" "}
                  <b>$0.005 per additional credit</b>. Your account manager
                  can share utilization-management tools to help the group
                  stay within the included allotment.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  Can different locations choose different engagement models?
                </h3>
                <p className="faq-a">
                  Yes — each location can independently elect either{" "}
                  <b>Continuous Integration</b> (Asha layers on top of your
                  existing PMS) or <b>PMS</b> (Asha's AI-Native PMS replaces
                  your existing system). The flat per-location rate of{" "}
                  <b>$1,998 / month all-in</b> applies either way under this
                  quote.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">How are locations determined?</h3>
                <p className="faq-a">
                  Locations follow exactly how your existing practice
                  management system has them configured. Asha maps your data
                  over one-to-one during onboarding, so{" "}
                  <b>locations cannot be merged or separated</b> as part of
                  the engagement — whatever structure exists in your current
                  PMS is what we mirror in Asha.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  Which PMS systems does Continuous Integration support?
                </h3>
                <p className="faq-a">
                  The Continuous Integration model supports{" "}
                  <b>Dentrix</b>, <b>Eaglesoft</b>, and <b>Open Dental</b>.
                  Other PMS systems are evaluated on a case-by-case basis.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SLIDE NAV */}
        <div className="slide-nav" aria-label="Slide navigation">
          <button
            type="button"
            className="slide-nav-btn"
            onClick={goPrev}
            disabled={currentSlide === 0}
            aria-label="Previous slide"
          >
            <span aria-hidden="true">←</span> Previous
          </button>

          <div className="slide-progress">
            <div className="slide-dots" role="tablist" aria-label="Slides">
              {SLIDES.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  className={`slide-dot ${
                    i === currentSlide ? "active" : ""
                  }`}
                  aria-label={`Go to slide ${i + 1}: ${s.label}`}
                  aria-selected={i === currentSlide}
                  onClick={() => setCurrentSlide(i)}
                />
              ))}
            </div>
            <div className="slide-counter">
              {String(currentSlide + 1).padStart(2, "0")} /{" "}
              {String(SLIDES.length).padStart(2, "0")} ·{" "}
              <b>{SLIDES[currentSlide].label}</b>
            </div>
          </div>

          <button
            type="button"
            className="slide-nav-btn primary"
            onClick={goNext}
            disabled={currentSlide === SLIDES.length - 1}
            aria-label="Next slide"
          >
            Next <span aria-hidden="true">→</span>
          </button>
        </div>

        {/* TAIL */}
        <div className="tail">
          <span>Asha Health, Inc.</span>
          <span>
            Confidential — Summary of proposed commercial terms; not a binding
            agreement.
          </span>
          <span>
            Slide {String(currentSlide + 1).padStart(2, "0")} /{" "}
            {String(SLIDES.length).padStart(2, "0")}
          </span>
        </div>
      </div>

    </div>
  );
};

export default OsFees2To4LocPage;
